import { faFileLines, faFilePdf } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import axios from "axios";
import FileDownload from "js-file-download";
import moment from "moment";
import { Stack, Accordion, Button } from "react-bootstrap";
import { Link } from "react-router-dom";

import { useLoading } from "components/UI/LoadingSpinner/UseLoading";
import { useAuth } from "hooks/useAuth";

export default function SideInfo({ pp, files }) {
  const { authToken, permissions } = useAuth();
  const { showLoadingSpinner, hideLoadingSpinner } = useLoading();

  const downloadFile = (file) => {
    showLoadingSpinner("Opening file...");

    axios
      .get(
        `${import.meta.env.VITE_DJANGO_ENDPOINT}file/get_file/?file_id=${file.file_id}`,
        {
          withCredentials: true,
          headers: {
            Authorization: `Token ${authToken}`,
          },
          responseType: "blob",
        },
      )
      .then((response) => {
        hideLoadingSpinner();

        // Get the Content-Disposition header to determine if file should open inline or download
        const contentDisposition = response.headers["content-disposition"];
        const isInline =
          contentDisposition && contentDisposition.includes("inline");

        if (isInline) {
          // For inline files (protection-profile), open in new tab
          const blob = new Blob([response.data], {
            type: response.headers["content-type"],
          });
          const url = window.URL.createObjectURL(blob);
          window.open(url, "_blank");
          // Clean up the URL after a delay to allow the browser to open it
          setTimeout(() => window.URL.revokeObjectURL(url), 100);
        } else {
          // For attachment files, trigger download
          FileDownload(
            response.data,
            file.file_new_name ? file.file_new_name : file.file_name,
          );
        }
      })
      .catch((error) => {
        hideLoadingSpinner();
        console.log("Error downloading file:", error);
      });
  };

  return (
    <div>
      <Accordion
        flush
        className="border border-dark mb-3"
        data-cy="pp-important-dates"
      >
        <Accordion.Item>
          <Accordion.Header className="bg-secondary">
            <h6 className="text-white">IMPORTANT DATES</h6>
          </Accordion.Header>
          <Accordion.Body
            className="bg-light p-1"
            data-cy="pp-est-completion-date"
          >
            <Stack direction="horizontal" className="mb-2">
              <small className="me-3">Estimated Completion Date</small>
              <small className="text-break ms-auto">
                {pp.pp_estcompletiondate &&
                  moment.utc(pp.pp_estcompletiondate).format("MM/DD/YYYY")}
              </small>
            </Stack>
            <Stack
              direction="horizontal"
              className="mb-2"
              data-cy="pp-approval-date"
            >
              <small className="me-3">Approval Date</small>
              <small className="text-break ms-auto">
                {pp.pp_date && moment.utc(pp.pp_date).format("MM/DD/YYYY")}
              </small>
            </Stack>
            <Stack
              direction="horizontal"
              className="mb-2"
              data-cy="pp-transition-date"
            >
              <small className="me-3">Transition Date</small>
              <small className="text-break ms-auto">
                {pp.pp_transition &&
                  moment.utc(pp.pp_transition).format("MM/DD/YYYY")}
              </small>
            </Stack>
            <Stack
              direction="horizontal"
              className="mb-2"
              data-cy="pp-sunset-date"
            >
              <small className="me-3">Sunset Date</small>
              <small className="text-break ms-auto">
                {pp.sunset_date &&
                  moment.utc(pp.sunset_date).format("MM/DD/YYYY")}
              </small>
            </Stack>
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>
      <Accordion flush className="border border-dark mb-3">
        <Accordion.Item>
          <Accordion.Header className="bg-secondary">
            <h6 className="text-white">PP DOCUMENTS</h6>
          </Accordion.Header>
          <Accordion.Body className="bg-light p-1">
            {permissions?.role_type === "NIAP" && (
              <div style={{ textAlign: "center" }}>
                <Button
                  as={Link}
                  to={`/community/protectionprofiles/details/${pp?.pp_id}/protection-profile/files`}
                  variant="primary"
                >
                  View All
                </Button>
              </div>
            )}
            {files?.map((file, idx) => (
              <Stack
                direction="horizontal"
                className="d-flex justify-content-left align-items-start"
                key={idx}
              >
                {file.file_mime_type === "application/pdf" && (
                  <FontAwesomeIcon icon={faFilePdf} color="red" size="xl" />
                )}
                {file.file_mime_type !== "application/pdf" && (
                  <FontAwesomeIcon icon={faFileLines} color="gray" size="xl" />
                )}
                <div className="ms-2 text-break">
                  {file.file_display_name === "Protection Profile (HTML)" &&
                    file.file_name.includes(".htm") && (
                      <Button
                        style={{ overflowWrap: "break-word" }}
                        className="text-start m-0 p-0"
                        variant="link"
                        href={`/static_html${file.file_location}${file.file_name}`}
                        target="_blank"
                      >
                        {file.file_label
                          ? file.file_label
                          : file.file_new_name
                            ? file.file_new_name
                            : file.file_name}
                      </Button>
                    )}
                  {file.file_display_name === "Protection Profile (HTML)" &&
                    !file.file_name.includes(".htm") && (
                      <Button
                        style={{ overflowWrap: "break-word" }}
                        className="text-start m-0 p-0"
                        variant="link"
                        href={`/static_html${file.file_location}${file.file_name}/index.html`}
                        target="_blank"
                      >
                        {file.file_label
                          ? file.file_label
                          : file.file_new_name
                            ? file.file_new_name
                            : file.file_name}
                      </Button>
                    )}
                  {/* question for this one below, does this one have the potential to pull from somewhere other than protection-profile? */}
                  {file.file_display_name !== "Protection Profile (HTML)" &&
                    file.isFolder && (
                      <Button
                        style={{ overflowWrap: "break-word" }}
                        className="text-start m-0 p-0"
                        variant="link"
                        href={`/static_html${file.file_location}${file.file_name}/index.html`}
                        target="_blank"
                      >
                        {file.file_label
                          ? file.file_label
                          : file.file_new_name
                            ? file.file_new_name
                            : file.file_name}
                      </Button>
                    )}
                  {file.file_display_name === "Configuration Document" &&
                    !file.isFolder && (
                      <Button
                        variant="link"
                        onClick={() => downloadFile(file)}
                        className="text-start m-0 p-0"
                      >
                        {file.file_label ? file.file_label : file.file_name}
                      </Button>
                    )}
                  {file.file_display_name !== "Protection Profile (HTML)" &&
                    file.file_display_name !== "Configuration Document" &&
                    !file.isFolder && (
                      <Button
                        style={{ overflowWrap: "break-word" }}
                        className="text-start m-0 p-0"
                        variant="link"
                        onClick={() => downloadFile(file)}
                      >
                        {file.file_label
                          ? file.file_label
                          : file.file_new_name
                            ? file.file_new_name
                            : file.file_name}
                      </Button>
                    )}
                  <p className="mb-0 ms-3">
                    {file.file_new_name ? file.file_new_name : file.file_name}
                  </p>
                  <p className="mb-0 ms-3">
                    Attached by {file?.uploaded_by?.first_name}{" "}
                    {file?.uploaded_by?.last_name} on{" "}
                    {file?.uploaded_on &&
                      moment
                        .utc(file?.uploaded_on)
                        .format("YYYY.MM.DD [at] HHmm")}
                  </p>
                </div>
              </Stack>
            ))}
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>
      <Accordion flush className="border border-dark mb-3" data-cy="pp-info">
        <Accordion.Item>
          <Accordion.Header className="bg-secondary">
            <h6 className="text-white">PP INFORMATION</h6>
          </Accordion.Header>
          <Accordion.Body className="bg-light p-0">
            <Stack
              className="pb-2 ps-2 border border-dark"
              data-cy="pp-common-name"
            >
              <small>Common Name</small>
              <p className="text-break">{pp.pp_common_name}</p>
            </Stack>
            <Stack
              className="pb-2 ps-2 border border-dark"
              data-cy="pp-priority"
            >
              <small>Priority</small>
              <p className="text-break">{pp.pp_priority}</p>
            </Stack>
            <Stack
              className="pb-2 ps-2 border border-dark"
              data-cy="pp-sponsor"
            >
              <p className="text-break">{pp.pp_sponsor_id}</p>
            </Stack>
            <Stack
              className="pb-2 ps-2 border border-dark"
              data-cy="pp-mr-version"
            >
              <small>MR Version</small>
              <p className="text-break">{pp.mr_version}</p>
            </Stack>
            <Stack
              className="pb-2 ps-2 border border-dark"
              data-cy="pp-cc-version"
            >
              <small>CC Version</small>
              <p className="text</Link>-break">{pp.cc_version}</p>
            </Stack>
            <Stack
              className="pb-2 ps-2 border border-dark"
              data-cy="pp-successor"
            >
              <small>Successor</small>
              {pp.successor_id ? (
                <p className="text-break">
                  <Link
                    to={`/community/protectionprofiles/details/${pp.successor_id.pp_id}`}
                  >
                    {pp.successor_id?.pp_short_name}
                  </Link>
                </p>
              ) : (
                <p className="text-break">None</p>
              )}
            </Stack>
            <Stack
              className="pb-2 ps-2 border border-dark"
              data-cy="pp-predecessor"
            >
              <small>Predecessor</small>
              {pp.predecessor_id ? (
                <p className="text-break">
                  <Link
                    to={`/community/protectionprofiles/details/${pp.predecessor_id.pp_id}`}
                  >
                    {pp.predecessor_id?.pp_short_name}
                  </Link>
                </p>
              ) : (
                <p className="text-break">None</p>
              )}
            </Stack>
            <Stack className="pb-2 ps-2 border border-dark">
              <small>Record Created:</small>
              <p className="text-break">
                {pp.entrydate && new Date(pp.entrydate)?.toLocaleDateString()}
              </p>
            </Stack>
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>
    </div>
  );
}
